import {
   AfterViewChecked,
   Component,
   ElementRef,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   QueryList,
   Renderer2,
   SimpleChanges,
   ViewChildren
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { splitFilename } from '../../shared/helper-functions';
import { Mimetype } from '../../shared/mimetype';
import { Toast } from '../../shared/toast/toast';

@Component({
   selector: 'app-onedrive-list',
   templateUrl: './onedrive-list.component.html',
   styleUrls: ['./onedrive-list.component.css']
})
export class OnedriveListComponent<T = unknown> implements OnChanges, AfterViewChecked {
   @Input() selectedItems: ItemDMS<T>[] = [];
   @Input() itemList: {
      /** a custom ID to identify the list */
      id: string;
      items: ItemDMS<T>[];
   };
   @Input() showLoadMore: boolean = false;
   @Input() canDrag = true;

   /** Event emitted when the user clicks on the item's name to open the item. */
   @Output() openItem = new EventEmitter<RequestOpenEvent<T>>();
   /** Event emitted when the user moves the item. */
   @Output() moveItem = new EventEmitter<RequestMoveEvent<T>>();
   /** Event emitted when the user confirms the new name to rename the item. */
   @Output() renameConfirmed = new EventEmitter<RequestRenameEvent<T>>();
   /** Event emitted when the user clicks the "Download" button. */
   @Output() downloadClicked = new EventEmitter<RequestDownloadEvent<T>>();
   /** Event emitted when the user clicks the "Delete" button. */
   @Output() deleteClicked = new EventEmitter<RequestDownloadEvent<T>>();

   @ViewChildren('renameElement') renameElement: QueryList<ElementRef<HTMLInputElement>>;

   private itemState: Array<ItemState>;
   private itemEditPendingList: Array<number> = []; // Store the index of items that are in pending state
   nativeElement: any;
   loading: boolean;

   constructor(
      private translate: TranslateService,
      private toast: Toast,
      private renderer: Renderer2,
      private elementRef: ElementRef
   ) {
      this.nativeElement = this.elementRef.nativeElement;
   }

   ngAfterViewChecked() {
      /**
       * Check each input element (#renameElement) in the ngFor list.
       * If the associated item is currently being renamed (editing state === true),
       * focus on the corresponding input element.
       */
      this.renameElement.forEach((itemElement: ElementRef<HTMLInputElement>, index) => {
         const inputBox = this.renderer.selectRootElement(itemElement.nativeElement) as HTMLInputElement;
         const itemName = document.getElementById('link_openItem_' + index).innerHTML;
         if (this.getItemState(index, 'editing')) {
            inputBox.focus();
         } else {
            inputBox.value = itemName;
         }
      });
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (this.itemList && changes.itemList) {
         if (
            (changes.itemList.previousValue as typeof this.itemList)?.id !==
            (changes.itemList.currentValue as typeof this.itemList)?.id
         ) {
            this.itemState = new Array<ItemState>(this.itemList?.items?.length).fill(null).map(() => ({
               editing: EditStatus.Inactive,
               selected: false
            }));
         } else {
            const prevLength = (changes.itemList.previousValue as typeof this.itemList)?.items?.length || 0;
            const currLength = (changes.itemList.currentValue as typeof this.itemList)?.items?.length || 0;

            if (currLength > prevLength) {
               const newItemsLength = currLength - prevLength;
               const newItemStates = new Array<ItemState>(newItemsLength).fill(null).map(() => ({
                  editing: EditStatus.Inactive,
                  selected: false
               }));

               this.itemState = this.itemState ? [...this.itemState, ...newItemStates] : newItemStates;
            }
         }
      }
   }

   toggleLoading(flag: boolean) {
      this.loading = flag;
   }

   /** Return an icon image path of item type (e.g. text, video, image, pptx, pdf, folder, etc.) */
   getIcon(mimeType: string = '', name: string = '', isFolder: boolean = false) {
      return Mimetype.getIconDMSList(mimeType, name, isFolder);
   }

   onClickOpenItem($event: MouseEvent, item: ItemDMS<T>, index: number) {
      $event.stopPropagation();
      this.openItem.emit({ index: index, item: item });
   }

   //=============================================================================
   // ITEM STATE MANAGEMENT
   //=============================================================================

   getItemState(index: number, state: keyof ItemState) {
      if (!this.itemState) return false;
      return this.itemState[index][state];
   }

   private setStateEditing(index: number, status: ItemState['editing']) {
      const isInPendingList = this.itemEditPendingList.includes(index);
      if (status === EditStatus.Pending) {
         if (!isInPendingList) {
            this.itemEditPendingList.push(index);
         }
         this.itemState[index].editing = status;
         return;
      }

      // To make sure only one item is being edited at a time
      if (status === EditStatus.Active) {
         if (!isInPendingList && this.itemEditPendingList.length > 0) {
            return;
         }
         if (isInPendingList) {
            this.itemEditPendingList = this.itemEditPendingList.filter((i) => i !== index);
         }

         this.itemState.forEach((item_state) => {
            item_state.editing = EditStatus.Inactive;
         });
         this.itemState[index].editing = status;
         return;
      }

      if (status === EditStatus.Inactive) {
         if (!isInPendingList && this.itemEditPendingList.length > 0) {
            return;
         }
         if (isInPendingList) {
            this.itemEditPendingList = this.itemEditPendingList.filter((i) => i !== index);
         }
         this.itemState[index].editing = status;
         return;
      }
   }

   //=============================================================================
   // MENU BUTTONS' ACTION
   //=============================================================================

   onClickMove(item: ItemDMS<T>, index: number) {
      this.moveItem.emit({ index: index, itemToMove: item });
   }

   onClickDownload(item: ItemDMS<T>, index: number) {
      this.downloadClicked.emit({ index: index, item: item });
   }

   onClickRename(index: number) {
      const inputBox = document.getElementById('edit-' + index) as HTMLInputElement;
      const filename = this.decodeHtmlEntities(document.getElementById('link_openItem_' + index).innerHTML);
      if (this.itemList.items[index].isFolder) {
         inputBox.value = filename;
      } else {
         inputBox.value = splitFilename(filename)?.name || '';
      }
      this.setStateEditing(index, EditStatus.Active);
   }

   onClickDelete(item: ItemDMS<T>, index: number) {
      this.deleteClicked.emit({ index: index, item: item });
   }

   /**
    * Handles keypress events during renaming.
    *
    * @param $event
    * @param item
    * @param index
    */
   onKeyPressedDuringRename($event: KeyboardEvent, item: ItemDMS<T>, index: number) {
      if (!item) return;
      if (this.itemState[index].editing === EditStatus.Pending) {
         return;
      }

      switch ($event.key) {
         case 'Enter': {
            this.renameItem(item, index);
            break;
         }
         case 'Escape': {
            this.closeRenameBox(index);
            break;
         }
      }
   }

   /**
    * Call upon losing focus from the item renaming input box.
    */
   onLoseFocusEditing($event: FocusEvent, index: number, item: ItemDMS<T>) {
      // If (blur) Event was caused by clicking 'Rename' button on the #ItemMenu, ignore it
      if (($event.relatedTarget as HTMLElement)?.id.startsWith('btn_itemMenu_')) {
         return;
      }

      // If losing focus from clicking anywhere else, save the new name as if the user pressed 'Enter'.
      if (this.itemState[index].editing === EditStatus.Active) {
         this.renameItem(item, index);
      }
   }

   //=============================================================================
   // HANDLE RENAME INTERACTION
   //=============================================================================

   private renameItem(item: ItemDMS<T>, index: number) {
      if (!item) return;

      const element = document.getElementById('edit-' + index) as HTMLInputElement;
      const splitOldName = splitFilename(item.name);

      let extension = '';
      if (!item.isFolder && splitOldName.dotIndex !== -1) {
         extension = '.' + splitOldName.extension;
      }

      // If the input box is empty, keep the renaming box open and don't end the process.
      if (element?.value.length === 0) return;

      const newName = element.value + extension;
      const isNewNameValid = this.validateNewName(item.name, newName);

      if (!isNewNameValid) {
         this.closeRenameBox(index);
         return;
      }

      const trimmedNewName = element.value.trim() + extension;
      const isTrimmedNewNameValid = this.validateNewName(item.name, trimmedNewName);
      if (!isTrimmedNewNameValid) {
         this.closeRenameBox(index);
         return;
      }

      if (this.isContainInvalidCharacters(newName)) {
         this.translate.get('alert.create_folder_error').subscribe((s) => {
            this.toast.error(s);
         });
         return;
      }

      this.setStateEditing(index, EditStatus.Pending);
      this.renameConfirmed.emit({
         index: index,
         currentItem: item,
         newName: trimmedNewName,
         renameResultCallback: (status) => this.handleRenameResult(status, index)
      });
   }

   /**
    * Validates the new name based on several conditions:
    * - non-empty
    * - different from old name
    *
    * @param {string} oldName - The current name.
    * @param {string} newName - The proposed new name.
    * @returns {boolean} True if the new name is valid, false otherwise.
    */
   private validateNewName(oldName: string, newName: string): boolean {
      if (!newName || newName.length === 0) {
         return false;
      }
      if (oldName === newName) {
         return false;
      }
      return true;
   }

   private isContainInvalidCharacters(name: string): boolean {
      return /[<|>\\/:?*"]/g.test(name);
   }

   private handleRenameResult(status: 'success' | 'error', index: number) {
      switch (status) {
      case 'success':
         this.closeRenameBox(index);
         break;
      case 'error':
         this.setStateEditing(index, EditStatus.Active);
         break;
      default:
         const exhaustiveCheck: never = status;
         throw new Error(`Unhandled status case: ${exhaustiveCheck}`);
      }
   }

   private closeRenameBox(index: number) {
      this.setStateEditing(index, EditStatus.Inactive);
   }

   //=============================================================================
   // OTHER METHODS
   //=============================================================================

   /**
    * Converts HTML entities within a given string back to their original characters.
    *
    * @param {string} htmlString - The string possibly containing HTML entities.
    * @returns {string} The string with HTML entities decoded.
    */
   private decodeHtmlEntities(htmlString: string): string {
      const tempElement = document.createElement('textarea');
      tempElement.innerHTML = htmlString;
      return tempElement.value;
   }
}

type ItemState = {
   editing: EditStatus;
   selected: boolean;
};

enum EditStatus {
   /** Not editing */
   Inactive = 0,
   /** Currently editing */
   Active = 1,
   /** Waiting for confirmation from parent component or API */
   Pending = 2
}

export type ItemDMS<T = unknown> = {
   id?: string;
   name: string;
   mimeType: string;
   isFolder?: boolean;
   item?: T;
};

export type ItemList<T = unknown> = {
   /** a custom ID to identify the list */
   id: string;
   items: ItemDMS<T>[];
};

export type RequestOpenEvent<T = unknown> = {
   index: number;
   item: ItemDMS<T>;
};

export type RequestMoveEvent<T = unknown> = {
   index: number;
   itemToMove: ItemDMS<T>;
};

export type RequestRenameEvent<T = unknown> = {
   index: number;
   currentItem: ItemDMS<T>;
   newName: string;
   renameResultCallback: (status: 'success' | 'error') => void
};

export type RequestDownloadEvent<T = unknown> = {
   index: number;
   item: ItemDMS<T>;
};

export type RequestDeleteEvent<T = unknown> = {
   index: number;
   item: ItemDMS<T>;
};
